<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import ModalBase from "@/components/modal/ModalBase.vue";
import ArgonCalendar from "@/components/ArgonCalendar.vue";
import ArgonLoadingButton from "@/components/ArgonLoadingButton.vue";
import ArgonSnackbar from "@/components/ArgonSnackbar.vue";
import { dateUtils } from "@/utils";
import { stopStore } from "@/_store/stopStore";
import { errorUtils } from "@/utils";
const modalStopDate = ref(null);
const props = defineProps({
  modalId: { type: String, required: true },
  stopId: {
    type: String,
    default() {
      return null;
    },
  },
  stopIds: {
    type: Array,
    default() {
      return [];
    },
  },
});

const emit = defineEmits(["close", "success"]);

const vuexStore = useStore();
const bootstrap = vuexStore.state.bootstrap;
const stopsStore = stopStore();

const isLoading = ref(false);
const isSaving = ref(false);
const snackbar = ref(null);
const form = ref({
  date: "",
});

function toSafeString(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

function extractDatePart(value) {
  const text = toSafeString(value);
  if (!text) return "";
  return text.slice(0, 10);
}

const resolvedStopIds = computed(() => {
  if (props.stopIds.length > 0) return props.stopIds;
  return props.stopId ? [props.stopId] : [];
});

const resolvedStopId = computed(() => resolvedStopIds.value[0] || "");

const todayDate = computed(() => dateUtils.formatDate(new Date(), "YYYY-MM-DD"));
 
const rules = {
  date: {
    required: helpers.withMessage("La fecha es requerida.", required),
    validDateFormat: helpers.withMessage("La fecha no es válida.", (value) => {
      const selectedDate = extractDatePart(value);
      if (!selectedDate) return true;
      return /^\d{4}-\d{2}-\d{2}$/.test(selectedDate);
    }),
    isFutureDate: helpers.withMessage("La fecha debe ser igual o superior a hoy.", (value) => {
      const selectedDate = extractDatePart(value);
      if (!selectedDate) return true;
      return selectedDate >= todayDate.value;
    }),
  },
};
const v$ = useVuelidate(rules, form);

function closeSnackbar() {
  snackbar.value = null;
}

function showError(message) {
  snackbar.value = {
    title: "Error",
    message: message || "No se pudo guardar la fecha de la parada",
    type: "danger",
    color: "white",
  };
}

// function showInfo(message) {
//   snackbar.value = {
//     title: "Info",
//     message,
//     type: "info",
//     color: "white",
//   };
// }

function loadStopDetail() {
  const stopIdToLoad = resolvedStopId.value;
  if (!stopIdToLoad) {
    showError("Debe seleccionar al menos una parada para cambiar la fecha.");
    return;
  }

  isLoading.value = true;
  closeSnackbar();
  stopsStore.getStopById(stopIdToLoad);
}

async function validateForm() {
  await v$.value.$validate();
  return v$.value.$errors;
}

async function saveStopDate() {
  if (isSaving.value || isLoading.value) return;
  const validationErrors = await validateForm();
  if (validationErrors.length > 0) {
    let message = "";
    for (const value of validationErrors) {
      message = `${message}<li>${value.$message}</li>`;
    }
    if (message != "") {
      snackbar.value = {
        title: "Error",
        message: `<ul>${message}</ul>`,
        type: "danger",
        color: "white",
      };
    } 
  }
  else {
  
    isSaving.value = true;
    closeSnackbar();

    const stopIds = resolvedStopIds.value;
    if (stopIds.length === 0) {
      isSaving.value = false;
      showError("Debe seleccionar al menos una parada para cambiar la fecha.");
      return;
    }

    stopsStore
      .updateStopDate(stopIds, {
        date: extractDatePart(form.value.date),
      });
  }
}

function handleHide() {
  form.value.date = "";
  v$.value.$reset();
  emit("close");
}

watch(
  () => stopsStore.stopDateUpdated,
  (value) => {
    if (!isSaving.value || !value) return;
    isSaving.value = false;
    modalStopDate.value.closeModal();
    emit("success");
  }
);

watch(
  () => stopsStore.stop,
  (value) => {
    if (!isLoading.value || !value) return;
    form.value.date = extractDatePart(value?.date);
    v$.value.$reset();
    isLoading.value = false;
  }
);

watch(
  () => stopsStore.error,
  (value) => {
    if (!value) return;
    if (!isLoading.value && !isSaving.value) return;
    showError(errorUtils.getErrorMessageHtml(value, "No se pudo actualizar la fecha de la parada."));
    isLoading.value = false;
    isSaving.value = false;
  }
);

onMounted(() => {
  const modal = new bootstrap.Modal(document.getElementById(props.modalId), {
    keyboard: true,
    backdrop: true,
  });
  modal.show();
  loadStopDetail();
});
</script>

<template>
  <modal-base
    :id="modalId"
    ref="modalStopDate"
    width="25rem"
    @hide="handleHide"
  >
    <template #header>
      <h6 class="mb-0">Modificar fecha</h6>
    </template>

    <template #body>

      <div class="row align-items-center mb-0">
        <div class="col">
          <argon-calendar
            id="stop-edit-date"
            v-model="form.date"
            type="date"
            :error="v$.date.$error"
            :disabled="isLoading || isSaving"
          />
        </div>
      </div>
    </template>

    <template #footer>
      <argon-loading-button
        class="btn bg-gradient-primary btn-xs"
        color="primary"
        variant="gradient"
        :label="isSaving ? 'Guardando...' : 'Guardar'"
        :loading="isSaving"
        :disable-button="isLoading || isSaving"
        :error="v$.date.$error"

        @click="saveStopDate"
      />
    </template>

    <div class="position-fixed top-0 end-0 z-index-2 p-3">
      <argon-snackbar
        v-if="snackbar"
        :title="snackbar.title"
        :description="snackbar.message"
        :icon="{ component: 'ni ni-notification-70', color: snackbar.type }"
        :color="snackbar.color"
        :close-handler="closeSnackbar"
      />
    </div>
  </modal-base>
</template>
